import { dirname, extname, join } from "path";

import type { RelativeWorkspacePath } from "@codesandbox/pitcher-client";
import { ErrorBoundary } from "components/Error/ErrorBoundary";
import {
  relativePathToResource,
  useEnvironmentInterface,
} from "environment-interface";
import { useEditor } from "features/editor/Feature";
import { usePitcher } from "features/utils/usePitcher";
import defaultSchema from "hast-util-sanitize/lib/github.json";
import { useCapabilities } from "hooks/pitcher/useCapabilities";
import { useConfiguredTasks } from "hooks/pitcher/useConfiguredTasks";
import { useUnassignedPorts } from "hooks/pitcher/useUnassignedPorts";
import { Box, Button, Icon, Stack, Tooltip, styled } from "prism-react";
import React, { Children, useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import remarkGfm from "remark-gfm";
import { ITextModelService, StandaloneServices } from "vscode/services";

import { useActiveFile } from "hooks/useActiveFile";
import { useGetSelectedTextFromEditor } from "hooks/useGetSelectedTextFromEditor";
import { executeVSCodeCommand } from "utils/vscode";

import {
  codeStyle,
  getCodeRunCommand,
  getOpenPreviewDevtoolsAction,
  parseSelections,
  sanitizeCommand,
} from "./utils";

interface MarkdownPreviewProps {
  code: string;
  filepath?: RelativeWorkspacePath;
  /**
   * Add a copy and insert button on every snippet of code
   */
  enableCodeAction?: boolean;
  variant?: "neutral";
}

// eslint-disable-next-line
let SyntaxHighlighter: React.FC<any> | null = null;
let SyntaxHighlighterLoader: Promise<void> | undefined;

export const MarkdownPreview: React.FC<MarkdownPreviewProps> = React.memo(
  ({ code, filepath, enableCodeAction, variant }) => {
    const pitcher = usePitcher();
    const project = useEditor();
    const pitcherCapabilities = useCapabilities();
    const tasks = useConfiguredTasks();
    const unassignedPorts = useUnassignedPorts();
    const { clipboard } = useEnvironmentInterface();
    const getCurrentSelect = useGetSelectedTextFromEditor();
    const [activeFile] = useActiveFile();

    filepath ??= activeFile?.path;

    return (
      <ErrorBoundary context="MARKDOWN_PREVIEW">
        <MarkdownPreviewWrapper variant={variant}>
          <ReactMarkdown
            className="markdown-body"
            components={{
              a({ children, ...otherProps }) {
                const href = otherProps.href;
                if (href && href.startsWith("http://localhost:")) {
                  // Check if this is a link to a running preview devtool
                  const portRegex = /localhost:(\d+)/;
                  const matchRes = href.match(portRegex);
                  if (matchRes && matchRes[1]) {
                    const port = +matchRes[1];

                    const portInfo = getOpenPreviewDevtoolsAction(
                      port,
                      tasks,
                      unassignedPorts,
                    );

                    const parsedUrl = new URL(href);

                    if (portInfo) {
                      const newChildren = Children.map(children, (child) => {
                        if (
                          typeof child === "string" &&
                          child.startsWith("http://localhost:")
                        ) {
                          return child.replace(
                            /http:\/\/localhost:\d+/,
                            portInfo.port.url,
                          );
                        }

                        return child;
                      });
                      const realUrl =
                        "https://" +
                        portInfo.port.url +
                        parsedUrl.pathname +
                        (parsedUrl.search ? `?${parsedUrl.search}` : "");
                      return (
                        <Box
                          as="a"
                          css={{ display: "inline-flex", alignItems: "center" }}
                          // @ts-expect-error will be extracted into a custom component
                          onClick={(evt) => {
                            if (evt.ctrlKey || evt.metaKey) {
                              // if open in new tab don't do anything
                              return;
                            }

                            evt.preventDefault();
                            evt.stopPropagation();

                            executeVSCodeCommand(
                              "codesandbox.openCsbTab",
                              portInfo.params,
                            );
                          }}
                          {...otherProps}
                          href={realUrl}
                        >
                          <Stack as="span" gap={1} horizontal>
                            <span>{newChildren}</span>
                            <Icon size={4} type="preview" />
                          </Stack>
                        </Box>
                      );
                    }
                  }
                } else if (href && !href.startsWith("http")) {
                  const fullPathWithSelections =
                    filepath && href.startsWith("./")
                      ? join(dirname(filepath), href)
                      : href;

                  const [path, rawSelections] =
                    fullPathWithSelections.split("#");
                  const selections = parseSelections(rawSelections);
                  const relativePath =
                    pitcher.clients.fs.asRelativeWorkspacePath(path as string);
                  const fileId =
                    pitcher.clients.file.getFileIdFromPath(relativePath);
                  if (fileId) {
                    return (
                      <Box
                        as="a"
                        css={{
                          display: "inline-flex",
                          alignItems: "center",
                        }}
                        // @ts-expect-error will be extracted into a custom component
                        onClick={(evt) => {
                          evt.preventDefault();
                          evt.stopPropagation();

                          if (fileId) {
                            executeVSCodeCommand("codesandbox.openCsbTab", {
                              type: "FILE",
                              filepath: relativePath,
                              initialSelections: selections,
                            });
                          }
                        }}
                        {...otherProps}
                      >
                        <span>{children}</span>
                        <Icon size={4} type="file" />
                      </Box>
                    );
                  }
                }

                return (
                  <a rel="noopener noreferrer" target="_blank" {...otherProps}>
                    {children}
                  </a>
                );
              },
              pre({ children }) {
                return (
                  <Box as="pre" css={{ position: "relative" }}>
                    {children}
                  </Box>
                );
              },

              code({
                children,
                inline,
                className = "",
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                node,
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                ref,
                ...codeTagProps
              }) {
                // eslint-disable-next-line
                const [, setLoadedSyntaxHighlighter] = useState(
                  Boolean(SyntaxHighlighter),
                );

                // eslint-disable-next-line
                useEffect(() => {
                  if (!SyntaxHighlighterLoader) {
                    SyntaxHighlighterLoader = import(
                      // eslint-disable-next-line
                      // @ts-ignore
                      "react-syntax-highlighter/dist/esm/prism.js"
                    ).then((SyntaxHighlighterModule) => {
                      SyntaxHighlighter = SyntaxHighlighterModule.default;
                    });
                  }

                  SyntaxHighlighterLoader.then(() => {
                    setLoadedSyntaxHighlighter(true);
                  });
                }, []);

                if (!children) {
                  return (
                    <Box
                      as="code"
                      css={{
                        display: "flex",
                      }}
                    />
                  );
                }

                const match = /language-(\w+)/.exec(className || "") || [
                  "language-js",
                  "jsx",
                ];
                const code = String(children[0]);

                const runnableCode = sanitizeCommand(
                  getCodeRunCommand(code, className),
                );

                const preconfiguredTask = tasks.find(
                  (task) => task.command === runnableCode,
                );

                if (preconfiguredTask) {
                  // @TODO: change this to a more fine grained permission check in the future.
                  const onClick = () => {
                    if (preconfiguredTask.ports.length) {
                      const lowestPort = Math.min(
                        ...preconfiguredTask.ports.map((p) => p.port),
                      );

                      executeVSCodeCommand("codesandbox.openCsbTab", {
                        type: "TASK_PORT",
                        port: lowestPort,
                        taskId: preconfiguredTask.id,
                      });

                      return;
                    }

                    executeVSCodeCommand("codesandbox.terminal.open", {
                      taskId: preconfiguredTask.id,
                    });
                  };

                  if (inline) {
                    return (
                      <Box
                        as="code"
                        css={{
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                        }}
                        {...codeTagProps}
                        className="interactive"
                        onClick={onClick}
                      >
                        <span>{children}</span>
                        {/* Some hacky positioning because of the smaller font size in code blocks */}
                        <Box
                          as="span"
                          css={{
                            display: "flex",
                            marginTop: -2,
                            marginLeft: 1,
                          }}
                        >
                          <Icon size={4} type="run" />
                        </Box>
                      </Box>
                    );
                  }

                  return (
                    <Box
                      as="code"
                      css={{
                        display: "flex",
                      }}
                    >
                      {children}
                      {
                        <Box
                          as="span"
                          css={{
                            position: "absolute",
                            right: "$3",
                            top: "$3",
                          }}
                        >
                          <Tooltip
                            content={
                              pitcherCapabilities.task?.runCommand
                                ? undefined
                                : "This command cannot be run without write access."
                            }
                          >
                            <Button
                              css={{
                                color: "$neutral-fg-subtle",
                              }}
                              disabled={!pitcherCapabilities.task?.runCommand}
                              icon="run"
                              onClick={onClick}
                              variant="secondary"
                            />
                          </Tooltip>
                        </Box>
                      }
                    </Box>
                  );
                }

                if (!SyntaxHighlighter) {
                  return (
                    <Box
                      as="code"
                      css={{
                        display: "flex",
                      }}
                    >
                      {typeof children === "string"
                        ? children.replace(/\n$/, "")
                        : children}
                    </Box>
                  );
                }

                if (!inline && match) {
                  const syntaxHighlighter = (
                    <SyntaxHighlighter
                      children={String(children).replace(/\n$/, "")}
                      language={match[1]}
                      PreTag="div"
                      style={codeStyle}
                      {...codeTagProps}
                    />
                  );

                  if (enableCodeAction) {
                    return (
                      <>
                        <Stack
                          css={{
                            justifyContent: "flex-end",
                            position: "absolute",
                            right: "$2",
                            top: "$2",
                          }}
                          horizontal
                        >
                          <Tooltip content="Copy">
                            <Button
                              icon="copy"
                              iconSize={4}
                              onClick={() => {
                                clipboard.write(code);
                              }}
                            />
                          </Tooltip>

                          <Tooltip content="Insert code">
                            <Button
                              icon="add"
                              iconSize={4}
                              onClick={async () => {
                                const selection = await getCurrentSelect();
                                const model = await StandaloneServices.get(
                                  ITextModelService,
                                ).createModelReference(
                                  relativePathToResource(filepath!),
                                );

                                if (selection) {
                                  const position = selection.range;
                                  const range = {
                                    startColumn: position.endColumn,
                                    startLineNumber: position.endLineNumber,
                                    endColumn: position.endColumn,
                                    endLineNumber: position.endLineNumber,
                                  };

                                  if (model.object.textEditorModel) {
                                    model.object.textEditorModel.pushStackElement();
                                    model.object.textEditorModel.pushEditOperations(
                                      [],
                                      [{ range, text: code }],
                                      () => [],
                                    );
                                  }
                                }

                                model.dispose();
                              }}
                            />
                          </Tooltip>
                        </Stack>

                        <Box>{syntaxHighlighter}</Box>
                      </>
                    );
                  }

                  return syntaxHighlighter;
                }

                if (!code) {
                  return null;
                }

                const path =
                  filepath && code.startsWith("./")
                    ? join(dirname(filepath), code)
                    : code;

                const relativePath =
                  pitcher.clients.fs.asRelativeWorkspacePath(path);
                const fileId =
                  pitcher.clients.file.getFileIdFromPath(relativePath);

                // We got an actual file?
                if (fileId && extname(path)) {
                  codeTagProps.style = {
                    cursor: "pointer",
                  };
                  codeTagProps.onClick = () => {
                    executeVSCodeCommand("codesandbox.openCsbTab", {
                      type: "FILE",
                      filepath: relativePath,
                    });
                  };

                  return (
                    <Box
                      as="code"
                      className="interactive"
                      css={{
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                      }}
                      {...codeTagProps}
                    >
                      <span>{children}</span>
                      {/* Some hacky positioning because of the smaller font size in code blocks */}
                      <Box
                        as="span"
                        css={{ display: "flex", marginTop: -2, marginLeft: 1 }}
                      >
                        <Icon size={4} type="file" />
                      </Box>
                    </Box>
                  );
                }

                return <code {...codeTagProps}>{children}</code>;
              },
            }}
            rehypePlugins={[
              rehypeRaw,
              [
                rehypeSanitize,
                {
                  ...defaultSchema,
                  attributes: {
                    ...defaultSchema.attributes,
                    code: [
                      // List of all allowed languages:
                      [
                        "className",
                        "language-norun",
                        "language-js",
                        "language-jsx",
                        "language-ts",
                        "language-tsx",
                        "language-bash",
                        "language-css",
                        "language-md",
                      ],
                    ],
                  },
                },
              ],
            ]}
            remarkPlugins={[remarkGfm]}
            transformImageUri={(uri) => {
              if (!uri || project.type === "sandbox") return uri;

              const baseUrl = `https://github.com/${project.owner}/${project.repo}/raw/${project.branch}`;

              return uri.startsWith("http")
                ? uri
                : `${baseUrl}${uri.replace(/^\./, "")}`;
            }}
          >
            {code}
          </ReactMarkdown>
        </MarkdownPreviewWrapper>
      </ErrorBoundary>
    );
  },
);

const MarkdownPreviewWrapper = styled(Box, {
  height: "100%",
  overflow: "auto",
  userSelect: "text",
  paddingTop: "$2",
  px: "$2",

  variants: {
    variant: {
      neutral: {
        padding: 0,
        paddingTop: "$1",
        "& .markdown-body": {
          color: "$neutral-fg-high",
          fontSize: "$base",
          "--color-border-muted": "transparent",
        },
      },
    },
  },
});
